import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import FishListing from "./FishListing";
import Filters from "./Filters";
import ReactGA from "react-ga4";
import { useData } from "./contexts/DataContext";
import { useAuth } from "./contexts/AuthContext";
import { sizeNameMap } from "./utils/sizeNameUtils";
import { createImageProps } from "./utils/imageUtils";
import SEO from "./components/SEO";
import { useFeatureFlag, useFeatureFlagVariant } from "./hooks/useFeatureFlag";
import { usePostHogTracking } from "./hooks/usePostHogTracking";
import useSpeciesSearch from "./hooks/useSpeciesSearch";
import { useListingsDatabase } from "./hooks/useListingsDatabase";
import useListingGroupVisibility from "./hooks/useListingGroupVisibility";
import ComparisonPageFrame from "./components/listings/ComparisonPageFrame";
import {
  fishMatchesCategory,
  getReefWizardRecommendationScore,
  parsePhRange,
  parseTemperatureRange,
} from "./utils/filterUtils";
import {
  CARE_GUIDE_DIET_TRAIT_OPTIONS,
  DEFAULT_MAX_LIFESPAN_YEARS,
  getCareGuideDietTraits,
  getMaxNumericValueInText,
} from "./utils/careGuideQuickFacts";
import {
  filtersToUrlParams,
  urlParamsToFilters,
  ALL_FILTER_PARAM_NAMES,
  FILTER_PARAM_NAMES,
} from "./utils/urlParamUtils";
import { getFishProductUrl as getFishProductUrlUtil } from "./utils/urlUtils";

export const buildLivestockDeals = (groupedFish) => (
  Object.values(groupedFish || {})
    .flatMap((group) => {
      const sizeDeals = Object.entries(group.sizes_meta)
        .filter(([, sizeMeta]) =>
          sizeMeta.has_historical_data && parseFloat(sizeMeta.max_savings) > 0
        )
        .map(([size, sizeMeta]) => ({
          scientific_name: group.scientific_name,
          common_name: group.common_name,
          size,
          min_price: parseFloat(sizeMeta.min_price),
          max_savings: parseFloat(sizeMeta.max_savings),
          max_savings_percentage: parseFloat(sizeMeta.max_savings_percentage),
          historical_comparison_price: sizeMeta.historical_comparison_price,
          current_comparison_price: Number(sizeMeta.current_comparison_price),
          current_savings_percentage: Number(sizeMeta.current_max_savings_percentage),
          is_freshwater: group.is_freshwater,
          is_saltwater: group.is_saltwater,
          image_url: group.image_url,
          current_best_listing: group.sizes[size] ? group.sizes[size][0] : null,
        }));

      if (!sizeDeals.length) return [];
      return [
        sizeDeals.reduce((best, current) =>
          current.max_savings > best.max_savings ? current : best
        ),
      ];
    })
    .sort((a, b) => b.max_savings - a.max_savings)
    .slice(0, 20)
);

export const renderLivestockDealText = ({
  deal,
  context,
  isMobile,
}) => {
  const { cachedImageFailed, imageLoaded } = context;
  const displayPrice = deal.min_price.toFixed(2);
  const savingsPercentage = Math.round(deal.max_savings_percentage);
  const hasKnownSize = deal.size !== -1 && deal.size !== "-1";
  const textClassName =
    "deal-carousel-copy flex flex-col justify-center " +
    (!isMobile &&
    cachedImageFailed[deal.scientific_name] &&
    !imageLoaded[deal.scientific_name]
      ? "min-h-60"
      : "");

  return (
    <div className={textClassName}>
      <h3 className="text-lg font-bold text-white">
        {deal.common_name}
      </h3>
      <p className="text-sm text-slate-200 italic">
        {deal.scientific_name}
      </p>
      <p>
        {hasKnownSize
          ? (sizeNameMap[deal.size] || deal.size) +
            (isNaN(parseInt(deal.size)) ? "" : '"')
          : "Unknown Size"}
      </p>
      <div className="mt-2 mb-2">
        <span className="line-through text-slate-300 mr-2">
          ${deal.historical_comparison_price.toFixed(2)}
        </span>
        <span className="text-emerald-300 font-bold mr-2">
          ${displayPrice}
        </span>
        <span className="text-rose-300 font-bold">
          {"("}
          Typical: {savingsPercentage}%{")"}
        </span>
      </div>
      {Number.isFinite(deal.current_savings_percentage) && Number.isFinite(deal.current_comparison_price) &&
        deal.current_comparison_price > 0 && <p className="text-xs text-slate-200">
          Today: {Math.round(deal.current_savings_percentage)}% · today's average ${deal.current_comparison_price.toFixed(2)}
        </p>}
    </div>
  );
};

const getSortOrderFromExperiment = (experimentVariant) => {
  const sortOrderMap = {
    control: "mostListings",
    "most-dollars-saved": "mostDollarsSaved",
    "biggest-percentage-saved": "biggestPercentageSaved",
    "lowest-best-price": "lowestBestPrice",
    "hardest-to-find": "hardestToFind",
  };

  return sortOrderMap[experimentVariant] || "mostListings";
};

const ListingPriceComparison = ({
  listingData,
  dataType = "fish",
  seoTitle,
  seoDescription,
  seoKeywords,
  seoCanonical,
  headingFiltered,
  descriptionFiltered,
  headingUnfiltered,
  descriptionUnfiltered,
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Get data and setter functions from context
  const {
    categoryData,
    storesData,
    dataLoaded,
    apiError,
    searchSpecies,
    historicalPriceData,
    userFavorites,
    userSubscriptions,
    fetchUserFavorites,
    fetchUserSubscriptions,
    reefWizardRecommendations,
    reefWizardTanks,
    fetchReefWizardRecommendations,
    fetchReefWizardTanks,
    careGuidesData,
    careGuidesMap,
    retryAll,
    fishDigest,
    invertDigest,
    plantDigest,
    BACKEND_URL,
  } = useData();

  const { isAuthenticated, token } = useAuth();
  const { trackEvent } = usePostHogTracking();

  const defaultSortExperiment = useFeatureFlagVariant("default-sort-order-experiment");
  const showPriceDigest = useFeatureFlag("show-price-digest");

  const [error, setError] = useState(null);

  const IS_DEBUG = window._env_?.REACT_IS_DEBUG === "true";

  function getFishProductUrl(scientific_name, size, url) {
    return getFishProductUrlUtil(BACKEND_URL, scientific_name, size, url);
  }

  const speciesMap = {};
  listingData.forEach((fish) => {
    speciesMap[fish.scientific_name] = fish.common_name;
  });

  // Create a list of stores that actually have listings
  const availableStores = useMemo(() => {
    if (!listingData.length) return [];
    const storeSet = new Set();
    listingData.forEach((fish) => {
      if (fish.source) {
        storeSet.add(fish.source);
      }
    });
    return Array.from(storeSet);
  }, [listingData]);

  // Track whether stores have been initialized to prevent flickering
  const [storesInitialized, setStoresInitialized] = useState(false);

  useEffect(() => {
    // Initialize allowedStores with store IDs that have listings when data is loaded
    if (availableStores.length > 0 && !storesInitialized) {
      setAllowedStores(availableStores);
      setStoresInitialized(true);
    }
  }, [availableStores, storesInitialized]);

  useEffect(() => {
    ReactGA.initialize(
      [
        { trackingId: "AW-16954262035" }, // Google Ads tracking ID
        { trackingId: "G-Y38ESNYRXW" }, // Google Analytics tracking ID
      ],
      { testMode: IS_DEBUG }
    );
  }, []);

  // Track the default sort order experiment variant
  useEffect(() => {
    if (defaultSortExperiment) {
      trackEvent("experiment_exposed", {
        experiment_name: "default-sort-order-experiment",
        variant: defaultSortExperiment,
        default_sort_order: getSortOrderFromExperiment(defaultSortExperiment),
      });
      setSortOrder(getSortOrderFromExperiment(defaultSortExperiment));
    }
  }, [defaultSortExperiment]);

  // Fetch user favorites and subscriptions when authenticated
  useEffect(() => {
    if (isAuthenticated && token) {
      fetchUserFavorites(token);
      fetchUserSubscriptions(token);
      fetchReefWizardRecommendations(token);
      fetchReefWizardTanks(token);
    }
  }, [isAuthenticated, token]);

  const [searchTerm, setSearchTerm] = useState("");
  const [scientificFilter, setScientificFilter] = useState("");
  const [sizeFilter, setSizeFilter] = useState([0, 36]); // Actual size filter used for filtering
  const [tempSizeFilter, setTempSizeFilter] = useState([0, 36]); // Temporary size filter for slider
  const [sizeRange, setSizeRange] = useState([0, 36]); // Dynamic min and max for the slider
  const [minSize, setMinSize] = useState(0);
  const [maxSize, setMaxSize] = useState(36);
  const [availableNonNumericSizes, setAvailableNonNumericSizes] = useState(Object.values(sizeNameMap));
  const [nonNumericSizeFilter, setNonNumericSizeFilter] = useState(Object.values(sizeNameMap));
  const [includeNumericSizes, setIncludeNumericSizes] = useState(true);
  const [includeNonNumericSizes, setIncludeNonNumericSizes] = useState(true);
  const debounceTimeout = useRef(null);

  const debounce = (func, delay) => {
    return (...args) => {
      clearTimeout(debounceTimeout.current);
      debounceTimeout.current = setTimeout(() => func(...args), delay);
    };
  };

  const handleSizeFilterChange = useMemo(() => {
    return debounce((values) => {
      setSizeFilter(values);
    }, 1000);
  }, []);

  // Update tempSizeFilter immediately on slider change
  const handleTempSizeFilterChange = (values) => {
    setTempSizeFilter(values);
    handleSizeFilterChange(values); // Debounced update for sizeFilter
  };

  const isSizeFilterApplied = useMemo(() => {
    return sizeFilter[0] > minSize || sizeFilter[1] < maxSize;
  }, [sizeFilter, minSize, maxSize]);

  const [genusFilter, setGenusFilter] = useState("");
  const [familyFilter, setFamilyFilter] = useState("");
  const [orderFilter, setOrderFilter] = useState("");
  const [classFilter, setClassFilter] = useState("");
  const [phylumFilter, setPhylumFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [allowedStores, setAllowedStores] = useState(null);
  const [freshwaterFilter, setFreshwaterFilter] = useState(true);
  const [saltwaterFilter, setSaltwaterFilter] = useState(true);
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [notificationsOnly, setNotificationsOnly] = useState(false);
  const [reefWizardOnly, setReefWizardOnly] = useState(false);
  const [reefWizardTankFilter, setReefWizardTankFilter] = useState(null);

  // Care Requirements filter state
  const [minTankSizeMax, setMinTankSizeMax] = useState(null);
  const [maxSizeMax, setMaxSizeMax] = useState(null);
  const [temperatureRange, setTemperatureRange] = useState(null);
  const [phRange, setPhRange] = useState(null);
  const [careLevels, setCareLevels] = useState([]);
  const [reefSafeValues, setReefSafeValues] = useState([]);
  const [plantSafeValues, setPlantSafeValues] = useState([]);
  const [temperamentValues, setTemperamentValues] = useState([]);
  const [dietTraits, setDietTraits] = useState([]);
  const [lifespanRange, setLifespanRange] = useState(null);
  const [hasCareGuide, setHasCareGuide] = useState("all");

  // Compute available care requirement options from careGuidesData
  const {
    availableCareLevels,
    availableReefSafe,
    availablePlantSafe,
    availableTemperaments,
    availableDietTraits,
    temperatureBounds,
    maxTankSizeBound,
    maxSizeBound,
    phBounds,
    lifespanBounds,
  } = useMemo(() => {
    const careLevelSet = new Set();
    const reefSafeSet = new Set();
    const plantSafeSet = new Set();
    const temperamentSet = new Set();
    const dietTraitSet = new Set();
    let minTemp = 60;
    let maxTemp = 90;
    let maxTank = 500;
    let maxSize = 0;
    let minPh = null;
    let maxPh = null;
    let maxNumericLifespan = 0;

    careGuidesData.forEach((g) => {
      const s = g.summary || {};
      if (s.care_level) careLevelSet.add(s.care_level.toLowerCase());
      if (s.reef_safe) reefSafeSet.add(s.reef_safe.toLowerCase());
      if (s.plant_safe) plantSafeSet.add(s.plant_safe.toLowerCase());
      if (s.temperament) temperamentSet.add(s.temperament.toLowerCase());
      if (s.diet) {
        getCareGuideDietTraits(s.diet).forEach((trait) => dietTraitSet.add(trait));
      }
      if (s.lifespan) {
        const maxLifespanValue = getMaxNumericValueInText(s.lifespan);
        if (maxLifespanValue && maxLifespanValue > maxNumericLifespan) {
          maxNumericLifespan = maxLifespanValue;
        }
      }
      if (s.min_tank_size_gallons && s.min_tank_size_gallons > maxTank) maxTank = s.min_tank_size_gallons;
      if (s.max_size_inches && Number(s.max_size_inches) > maxSize) {
        maxSize = Number(s.max_size_inches);
      }
      if (s.temperature_range) {
        const parsed = parseTemperatureRange(s.temperature_range);
        if (parsed) {
          if (parsed[0] < minTemp) minTemp = parsed[0];
          if (parsed[1] > maxTemp) maxTemp = parsed[1];
        }
      }
      if (s.ph_range) {
        const parsed = parsePhRange(s.ph_range);
        if (parsed) {
          if (minPh === null || parsed[0] < minPh) minPh = parsed[0];
          if (maxPh === null || parsed[1] > maxPh) maxPh = parsed[1];
        }
      }
    });

    const careLevelOrder = ["beginner", "easy", "intermediate", "moderate", "advanced", "expert", "difficult"];
    const sortByOrder = (a, b, order) => {
      const ia = order.indexOf(a);
      const ib = order.indexOf(b);
      if (ia === -1 && ib === -1) return a.localeCompare(b);
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    };

    // Round temperature bounds to nearest 5
    minTemp = Math.floor(minTemp / 5) * 5;
    maxTemp = Math.ceil(maxTemp / 5) * 5;
    // Round max tank size to nearest 50
    maxTank = Math.ceil(maxTank / 50) * 50;
    maxSize = Math.ceil(maxSize * 2) / 2;
    if (minPh !== null) {
      minPh = Math.floor(minPh * 10) / 10;
    }
    if (maxPh !== null) {
      maxPh = Math.ceil(maxPh * 10) / 10;
    }

    return {
      availableCareLevels: Array.from(careLevelSet).sort((a, b) => sortByOrder(a, b, careLevelOrder)),
      availableReefSafe: Array.from(reefSafeSet).sort(),
      availablePlantSafe: Array.from(plantSafeSet).sort(),
      availableTemperaments: Array.from(temperamentSet).sort(),
      availableDietTraits: CARE_GUIDE_DIET_TRAIT_OPTIONS.map(
        (trait) => trait.value
      ).filter((value) => dietTraitSet.has(value)),
      temperatureBounds: [minTemp, maxTemp],
      maxTankSizeBound: maxTank,
      maxSizeBound: maxSize > 0 ? maxSize : 24,
      phBounds: [
        minPh !== null ? minPh : 6,
        maxPh !== null ? maxPh : 9,
      ],
      lifespanBounds: [
        1,
        maxNumericLifespan > 0
          ? maxNumericLifespan
          : DEFAULT_MAX_LIFESPAN_YEARS,
      ],
    };
  }, [careGuidesData]);

  // Build careFilters object for filter functions
  const careFilters = useMemo(() => ({
    minTankSizeMax,
    maxSizeMax,
    temperatureRange,
    phRange,
    careLevels,
    reefSafeValues,
    plantSafeValues,
    temperamentValues,
    dietTraits,
    lifespanRange,
    maxLifespanYears: lifespanBounds[1],
  }), [
    minTankSizeMax,
    maxSizeMax,
    temperatureRange,
    phRange,
    careLevels,
    reefSafeValues,
    plantSafeValues,
    temperamentValues,
    dietTraits,
    lifespanRange,
    lifespanBounds,
  ]);

  const searchSortOrderName = "bestSearchMatch";
  const reefWizardSortOrderName = "mostRecentRecommendation";
  const previousSortOrderRef = useRef(null);

  const { searchResults, loadingSearch, searchResultsForTermRef } = useSpeciesSearch(
    searchTerm,
    searchSpecies,
    {
      includeFish: dataType === "fish",
      includeInvertebrates: dataType === "inverts",
      includePlants: dataType === "plants",
      debounceMs: 500,
      onSearchStart: () => {
        if (sortOrder !== searchSortOrderName) {
          previousSortOrderRef.current = sortOrder;
          setSortOrder(searchSortOrderName);
        }
      },
      onSearchClear: () => {
        if (previousSortOrderRef.current) {
          setSortOrder(previousSortOrderRef.current);
          previousSortOrderRef.current = null;
        }
      },
    }
  );

  // Use the database hook for optimized filtering
  const dbFilters = useMemo(
    () => ({
      scientificFilter,
      genusFilter,
      familyFilter,
      orderFilter,
      classFilter,
      phylumFilter,
      allowedStores,
      freshwaterFilter,
      saltwaterFilter,
      categoryFilter,
      searchResults,
      searchTerm: searchTerm && searchTerm === searchResultsForTermRef.current ? searchTerm : "", // Only apply search filter if results match current term
      favoritesOnly,
      notificationsOnly,
      userFavorites,
      userSubscriptions,
      reefWizardOnly,
      reefWizardTankFilter,
      reefWizardRecommendations,
      sizeFilter,
      includeNumericSizes,
      includeNonNumericSizes,
      nonNumericSizeFilter,
      careGuidesMap,
      careFilters,
      hasCareGuide,
    }),
    [
      scientificFilter,
      genusFilter,
      familyFilter,
      orderFilter,
      classFilter,
      phylumFilter,
      allowedStores,
      freshwaterFilter,
      saltwaterFilter,
      categoryFilter,
      categoryData,
      searchResults,
      searchTerm,
      searchResultsForTermRef.current,
      favoritesOnly,
      notificationsOnly,
      userFavorites,
      userSubscriptions,
      reefWizardOnly,
      reefWizardTankFilter,
      reefWizardRecommendations,
      sizeFilter,
      includeNumericSizes,
      includeNonNumericSizes,
      nonNumericSizeFilter,
      careGuidesMap,
      careFilters,
      hasCareGuide,
    ]
  );

  const {
    filteredListings: filteredFishData,
    groupedFish,
    isQuerying: dbQuerying,
  } = useListingsDatabase(
    listingData,
    dataType,
    dbFilters,
    minSize,
    maxSize,
    availableStores,
    availableNonNumericSizes,
    storesInitialized,
    categoryData,
    historicalPriceData,
    true, // includeGrouping
    !dataLoaded[dataType],
    !dataLoaded.historicalPrices
  );

  // Query excluding size filters (for size slider range calculation)
  const dbFiltersExcludingSize = useMemo(
    () => ({
      scientificFilter,
      genusFilter,
      familyFilter,
      orderFilter,
      classFilter,
      phylumFilter,
      allowedStores,
      freshwaterFilter,
      saltwaterFilter,
      categoryFilter: categoryFilter,
      searchResults,
      searchTerm: searchTerm && searchTerm === searchResultsForTermRef.current ? searchTerm : "", // Only apply search filter if results match current term
      favoritesOnly,
      notificationsOnly,
      userFavorites,
      userSubscriptions,
      reefWizardOnly,
      reefWizardTankFilter,
      reefWizardRecommendations,
      sizeFilter: [0, 36], // Don't apply size filter
      includeNumericSizes: true,
      includeNonNumericSizes: true,
      nonNumericSizeFilter: Object.values(sizeNameMap),
      careGuidesMap,
      careFilters,
      hasCareGuide,
    }),
    [
      scientificFilter,
      genusFilter,
      familyFilter,
      orderFilter,
      classFilter,
      phylumFilter,
      allowedStores,
      freshwaterFilter,
      saltwaterFilter,
      categoryFilter,
      categoryData,
      searchResults,
      searchTerm,
      searchResultsForTermRef.current,
      favoritesOnly,
      notificationsOnly,
      userFavorites,
      userSubscriptions,
      reefWizardOnly,
      reefWizardTankFilter,
      reefWizardRecommendations,
      careGuidesMap,
      careFilters,
      hasCareGuide,
    ]
  );

  const {
    filteredListings: filteredFishDataExcludingSize,
    isQuerying: dbQueryingExcludingSize,
  } = useListingsDatabase(
    listingData,
    dataType,
    dbFiltersExcludingSize,
    0,
    36,
    availableStores,
    Object.values(sizeNameMap),
    storesInitialized,
    categoryData,
    historicalPriceData,
    false, // no grouping needed for size calculation
    !dataLoaded[dataType],
    !dataLoaded.historicalPrices
  );

  // Separate query for carousel that excludes species filter
  const dbFiltersForCarousel = useMemo(
    () => ({
      scientificFilter: "", // Exclude species filter for carousel
      genusFilter,
      familyFilter,
      orderFilter,
      classFilter,
      phylumFilter,
      allowedStores,
      freshwaterFilter,
      saltwaterFilter,
      categoryFilter: categoryFilter,
      searchResults,
      searchTerm: searchTerm && searchTerm === searchResultsForTermRef.current ? searchTerm : "", // Only apply search filter if results match current term
      favoritesOnly,
      notificationsOnly,
      userFavorites,
      userSubscriptions,
      reefWizardOnly,
      reefWizardTankFilter,
      reefWizardRecommendations,
      sizeFilter,
      includeNumericSizes,
      includeNonNumericSizes,
      nonNumericSizeFilter,
      careGuidesMap,
      careFilters,
      hasCareGuide,
    }),
    [
      genusFilter,
      familyFilter,
      orderFilter,
      classFilter,
      phylumFilter,
      allowedStores,
      freshwaterFilter,
      saltwaterFilter,
      categoryFilter,
      categoryData,
      searchResults,
      searchTerm,
      searchResultsForTermRef.current,
      favoritesOnly,
      notificationsOnly,
      userFavorites,
      userSubscriptions,
      reefWizardOnly,
      reefWizardTankFilter,
      reefWizardRecommendations,
      sizeFilter,
      includeNumericSizes,
      includeNonNumericSizes,
      nonNumericSizeFilter,
      careGuidesMap,
      careFilters,
      hasCareGuide,
    ]
  );

  const {
    filteredListings: filteredFishDataForCarousel,
    groupedFish: groupedFishForCarousel,
    isQuerying: dbQueryingForCarousel,
  } = useListingsDatabase(
    listingData,
    dataType,
    dbFiltersForCarousel,
    minSize,
    maxSize,
    availableStores,
    availableNonNumericSizes,
    storesInitialized,
    categoryData,
    historicalPriceData,
    true, // includeGrouping for carousel
    !dataLoaded[dataType],
    !dataLoaded.historicalPrices
  );

  // Combined state - if ANY query is in progress, show loading
  const isAnyDbQuerying = useMemo(
    () =>
      dbQuerying ||
      dbQueryingExcludingSize ||
      dbQueryingForCarousel,
    [
      dbQuerying,
      dbQueryingExcludingSize,
      dbQueryingForCarousel,
    ]
  );

  // Use loading states with context awareness
  const loadingListings = useMemo(
    () => !dataLoaded[dataType],
    [dataLoaded, dataType]
  );

  function compareIndividualSize(a, b, sizeSelector) {
    if (a === b) {
      return 0;
    }
    // Otherwise, pick the best size per the sizeSelector
    var bestSize = sizeSelector(a, b);
    if (bestSize === a) {
      return -1;
    } else {
      return 1;
    }
  }

  function compareSizes(a, b, preferNumeric, sizeSelector) {
    const aSize = sizeSelector(
      ...Object.keys(a.sizes)
        .map(Number)
        .filter((size) => !isNaN(size) && size >= 0)
    );
    const bSize = sizeSelector(
      ...Object.keys(b.sizes)
        .map(Number)
        .filter((size) => !isNaN(size) && size >= 0)
    );
    // NaN sizes should be sorted separately
    if (!isFinite(aSize)) {
      if (!isFinite(bSize)) {
        // Use sizeNameMap to determine the order of NaN sizes
        const sizeOrder = Object.keys(sizeNameMap);
        const aIndex = sizeSelector(
          ...Object.keys(a.sizes).map((size) => {
            var sizeIndex = sizeOrder.indexOf(size.toString());
            if (sizeIndex === -1) {
              sizeIndex = sizeOrder.length;
            }
            return sizeIndex;
          })
        );
        const bIndex = sizeSelector(
          ...Object.keys(b.sizes).map((size) => {
            var sizeIndex = sizeOrder.indexOf(size.toString());
            if (sizeIndex === -1) {
              sizeIndex = sizeOrder.length;
            }
            return sizeIndex;
          })
        );
        return compareIndividualSize(aIndex, bIndex, sizeSelector);
      } else {
        return preferNumeric ? 1 : -1;
      }
    } else if (!isFinite(bSize)) {
      return preferNumeric ? -1 : 1;
    }
    return compareIndividualSize(aSize, bSize, sizeSelector);
  }

  function getNumberOfListings(fish) {
    return Object.keys(fish.sizes).reduce((sum, size) => {
      return sum + fish.sizes[size].length;
    }, 0);
  }

  function getNumberOfStores(fish) {
    const stores = new Set();
    Object.values(fish.sizes).forEach((sizeGroup) => {
      sizeGroup.forEach((listing) => {
        if (listing.source) {
          stores.add(listing.source);
        }
      });
    });
    return stores.size;
  }

  const DEFAULT_SORT_OPTIONS = {
    mostListings: (a, b) => {
      const aCount = getNumberOfListings(a);
      const bCount = getNumberOfListings(b);
      return bCount - aCount;
    },
    mostStores: (a, b) => {
      const storeCountDifference = getNumberOfStores(b) - getNumberOfStores(a);
      if (storeCountDifference !== 0) return storeCountDifference;
      return getNumberOfListings(b) - getNumberOfListings(a);
    },
    mostDollarsSaved: (a, b) => b.max_max_savings - a.max_max_savings,
    biggestPercentageSaved: (a, b) =>
      b.max_max_savings_percentage - a.max_max_savings_percentage,
    lowestBestPrice: (a, b) => a.min_min_price - b.min_min_price,
    highestBestPrice: (a, b) => b.min_min_price - a.min_min_price,
    smallestAvailableSize: (a, b) => compareSizes(a, b, true, Math.min),
    largestAvailableSize: (a, b) => compareSizes(a, b, true, Math.max),
    hardestToFind: (a, b) => a.max_unique_days_count - b.max_unique_days_count,
  };

  const sortOptions = useMemo(() => {
    const options = { ...DEFAULT_SORT_OPTIONS };
    if (searchResultsForTermRef.current === searchTerm && searchTerm) {
      options[searchSortOrderName] = (a, b) => {
        return b.search_rank - a.search_rank;
      };
    }
    if (reefWizardOnly && reefWizardRecommendations && reefWizardRecommendations.length > 0) {
      options[reefWizardSortOrderName] = (a, b) => {
        const aScore = getReefWizardRecommendationScore(a, reefWizardRecommendations);
        const bScore = getReefWizardRecommendationScore(b, reefWizardRecommendations);
        return bScore - aScore;
      };
    }
    return options;
  }, [searchResultsForTermRef.current, reefWizardOnly, reefWizardRecommendations]);

  const [sortOrder, setSortOrder] = useState(
    Object.keys(DEFAULT_SORT_OPTIONS)[0]
  );

  useEffect(() => {
    if (previousSortOrderRef.current && sortOrder !== searchSortOrderName && sortOrder !== reefWizardSortOrderName) {
      previousSortOrderRef.current = sortOrder;
    }
  }, [sortOrder]);

  // Switch to reef wizard sort order when filter is enabled
  useEffect(() => {
    if (reefWizardOnly) {
      if (sortOrder !== reefWizardSortOrderName && !previousSortOrderRef.current) {
        previousSortOrderRef.current = sortOrder;
        setSortOrder(reefWizardSortOrderName);
      }
    } else if (!reefWizardOnly && sortOrder === reefWizardSortOrderName && previousSortOrderRef.current) {
      setSortOrder(previousSortOrderRef.current);
      previousSortOrderRef.current = null;
    }
  }, [reefWizardOnly, sortOrder]);

  // Stage 1: Load filters from URL on component mount ONLY
  const [haveLoadedFiltersFromUrl, setHaveLoadedFiltersFromUrl] =
    useState(false);
  useEffect(() => {
    if (!availableStores.length || haveLoadedFiltersFromUrl) return;

    const params = new URLSearchParams(location.search);
    const filters = urlParamsToFilters(params, {
      allowedStores: availableStores,
      sortOrder: getSortOrderFromExperiment(defaultSortExperiment),
    });

    // Apply filters
    setSearchTerm(filters.searchTerm);
    setScientificFilter(filters.scientificFilter);
    setGenusFilter(filters.genusFilter);
    setFamilyFilter(filters.familyFilter);
    setOrderFilter(filters.orderFilter);
    setClassFilter(filters.classFilter);
    setPhylumFilter(filters.phylumFilter);
    setCategoryFilter(filters.categoryFilter);
    setSortOrder(filters.sortOrder);
    setSizeFilter([filters.sizeMin, filters.sizeMax]);
    setTempSizeFilter([filters.sizeMin, filters.sizeMax]);
    setIncludeNumericSizes(filters.includeNumericSizes);
    setIncludeNonNumericSizes(filters.includeNonNumericSizes);
    if (params.has(FILTER_PARAM_NAMES.NON_NUMERIC_SIZES)) {
      setNonNumericSizeFilter(filters.nonNumericSizeFilter);
    }

    // Boolean filters
    setFreshwaterFilter(filters.freshwaterFilter);
    setSaltwaterFilter(filters.saltwaterFilter);
    setFavoritesOnly(filters.favoritesOnly);
    setNotificationsOnly(filters.notificationsOnly);
    setReefWizardOnly(filters.reefWizardOnly);
    setReefWizardTankFilter(filters.reefWizardTankFilter);

    // Care requirement filters
    setMinTankSizeMax(filters.minTankSizeMax);
    setMaxSizeMax(filters.maxSizeMax);
    setTemperatureRange(filters.temperatureRange);
    setPhRange(filters.phRange);
    setCareLevels(filters.careLevels);
    setReefSafeValues(filters.reefSafeValues);
    setPlantSafeValues(filters.plantSafeValues);
    setTemperamentValues(filters.temperamentValues);
    setDietTraits(filters.dietTraits);
    setLifespanRange(filters.lifespanRange);
    setHasCareGuide(filters.hasCareGuide);

    // Store filters
    setAllowedStores(filters.allowedStores);

    setHaveLoadedFiltersFromUrl(true);
  }, [availableStores, defaultSortExperiment, haveLoadedFiltersFromUrl, location.search]);

  // Stage 2: Update URL when filters change
  useEffect(() => {
    if (!availableStores.length || !haveLoadedFiltersFromUrl) return; // Wait until availableStores is initialized and filters are loaded from URL

    const currentFilters = {
      searchTerm,
      scientificFilter,
      genusFilter,
      familyFilter,
      orderFilter,
      classFilter,
      phylumFilter,
      categoryFilter,
      sortOrder,
      sizeFilter,
      sizeMin: sizeFilter[0],
      sizeMax: sizeFilter[1],
      minSize,
      maxSize,
      includeNumericSizes,
      includeNonNumericSizes,
      nonNumericSizeFilter,
      freshwaterFilter,
      saltwaterFilter,
      favoritesOnly,
      notificationsOnly,
      reefWizardOnly,
      reefWizardTankFilter,
      allowedStores,
      minTankSizeMax,
      maxSizeMax,
      temperatureRange,
      phRange,
      careLevels,
      reefSafeValues,
      plantSafeValues,
      temperamentValues,
      dietTraits,
      lifespanRange,
      hasCareGuide,
    };

    // Get current filter parameters
    const newFilterParams = filtersToUrlParams(
      currentFilters,
      {
        allowedStores: availableStores,
        minSize,
        maxSize,
        availableNonNumericSizes,
        sortOrder:
          getSortOrderFromExperiment(defaultSortExperiment) ||
          Object.keys(DEFAULT_SORT_OPTIONS)[0],
      },
      true,
      storesInitialized
    );

    // Get current URL parameters (including non-filter ones)
    const currentParams = new URLSearchParams(location.search);

    // Get current filter parameters from URL
    const currentFilterParams = filtersToUrlParams(
      urlParamsToFilters(currentParams, {
        allowedStores: availableStores,
        sortOrder: getSortOrderFromExperiment(defaultSortExperiment),
        minSize,
        maxSize,
        availableNonNumericSizes,
      }),
      {
        allowedStores: availableStores,
        minSize,
        maxSize,
        availableNonNumericSizes,
        sortOrder:
          getSortOrderFromExperiment(defaultSortExperiment) ||
          Object.keys(DEFAULT_SORT_OPTIONS)[0],
      },
      true,
      storesInitialized
    );

    // Only update if filter parameters have changed
    if (newFilterParams.toString() !== currentFilterParams.toString()) {
      // Remove all filter parameters from current params
      ALL_FILTER_PARAM_NAMES.forEach((paramName) => {
        currentParams.delete(paramName);
      });

      // Add new filter parameters
      newFilterParams.forEach((value, key) => {
        currentParams.set(key, value);
      });

      navigate({ search: currentParams.toString() }, { replace: true });
    }
  }, [
    haveLoadedFiltersFromUrl,
    searchTerm,
    scientificFilter,
    genusFilter,
    familyFilter,
    orderFilter,
    classFilter,
    phylumFilter,
    categoryFilter,
    sortOrder,
    sizeFilter,
    minSize,
    maxSize,
    includeNumericSizes,
    includeNonNumericSizes,
    nonNumericSizeFilter,
    freshwaterFilter,
    saltwaterFilter,
    favoritesOnly,
    notificationsOnly,
    reefWizardOnly,
    reefWizardTankFilter,
    allowedStores,
    minTankSizeMax,
    maxSizeMax,
    temperatureRange,
    phRange,
    careLevels,
    reefSafeValues,
    plantSafeValues,
    temperamentValues,
    dietTraits,
    lifespanRange,
    hasCareGuide,
    availableStores,
    storesInitialized,
    defaultSortExperiment,
    navigate,
    location.search,
  ]);

  useEffect(() => {
    if (categoryFilter) {
      ReactGA.event({
        category: "Filters",
        action: "Set Category Filter",
        label: categoryFilter,
      });
    }
  }, [categoryFilter]);

  useEffect(() => {
    if (allowedStores) {
      ReactGA.event({
        category: "Filters",
        action: "Set Store Filter",
        label: allowedStores.join(", "),
      });
    }
  }, [allowedStores]);

  useEffect(() => {
    if (scientificFilter) {
      ReactGA.event({
        category: "Filters",
        action: "Set Species Filter",
        label: scientificFilter,
      });
    }
  }, [scientificFilter]);

  useEffect(() => {
    const taxonFilters = {
      genus: genusFilter,
      family: familyFilter,
      order: orderFilter,
      class: classFilter,
      phylum: phylumFilter,
    };
    Object.entries(taxonFilters).forEach(([taxon, value]) => {
      if (value) {
        ReactGA.event({
          category: "Filters",
          action: `Set ${
            taxon.charAt(0).toUpperCase() + taxon.slice(1)
          } Filter`,
          label: value,
        });
      }
    });
  }, [genusFilter, familyFilter, orderFilter, classFilter, phylumFilter]);

  const isFiltered = useMemo(() => {
    const currentFilters = {
      searchTerm,
      scientificFilter,
      genusFilter,
      familyFilter,
      orderFilter,
      classFilter,
      phylumFilter,
      categoryFilter,
      sizeFilter,
      sizeMin: sizeFilter[0],
      sizeMax: sizeFilter[1],
      minSize,
      maxSize,
      includeNumericSizes,
      includeNonNumericSizes,
      nonNumericSizeFilter,
      freshwaterFilter,
      saltwaterFilter,
      favoritesOnly,
      notificationsOnly,
      reefWizardOnly,
      reefWizardTankFilter,
      allowedStores,
      minTankSizeMax,
      maxSizeMax,
      temperatureRange,
      phRange,
      careLevels,
      reefSafeValues,
      plantSafeValues,
      temperamentValues,
      dietTraits,
      lifespanRange,
      hasCareGuide,
    };

    // Get current filter parameters
    const params = filtersToUrlParams(
      currentFilters,
      {
        allowedStores: availableStores,
        availableNonNumericSizes: availableNonNumericSizes,
        minSize,
        maxSize,
        sortOrder:
          getSortOrderFromExperiment(defaultSortExperiment) ||
          Object.keys(DEFAULT_SORT_OPTIONS)[0],
      },
      true,
      storesInitialized
    );

    return params.toString().length > 0;
  }, [
    searchTerm,
    scientificFilter,
    includeNumericSizes,
    isSizeFilterApplied,
    includeNonNumericSizes,
    nonNumericSizeFilter,
    genusFilter,
    familyFilter,
    orderFilter,
    classFilter,
    phylumFilter,
    categoryFilter,
    allowedStores,
    availableStores,
    freshwaterFilter,
    saltwaterFilter,
    favoritesOnly,
    notificationsOnly,
    reefWizardOnly,
    reefWizardTankFilter,
    minTankSizeMax,
    maxSizeMax,
    temperatureRange,
    phRange,
    careLevels,
    reefSafeValues,
    plantSafeValues,
    temperamentValues,
    dietTraits,
    lifespanRange,
    hasCareGuide,
    availableNonNumericSizes,
  ]);

  // Get unique values for the filter dropdowns - memoized for performance
  // filteredX: values that exist in the currently filtered results (shown normally)
  // unfilteredX: values that exist in all data but not in filtered results (shown grayed out)
  const filteredScientificNames = useMemo(
    () =>
      [...new Set(filteredFishData.map((item) => item.scientific_name))].sort(),
    [filteredFishData]
  );

  const filteredSizes = useMemo(
    () =>
      [...new Set(filteredFishData.map((item) => item.size))].sort(
        (a, b) => parseInt(a) - parseInt(b)
      ),
    [filteredFishData]
  );

  const filteredGenera = useMemo(
    () =>
      [
        ...new Set(
          filteredFishData.map((item) => item.genus_name).filter((name) => name)
        ),
      ].sort(),
    [filteredFishData]
  );

  const filteredFamilies = useMemo(
    () =>
      [
        ...new Set(
          filteredFishData
            .map((item) => item.family_name)
            .filter((name) => name)
        ),
      ].sort(),
    [filteredFishData]
  );

  const filteredOrders = useMemo(
    () =>
      [
        ...new Set(
          filteredFishData.map((item) => item.order_name).filter((name) => name)
        ),
      ].sort(),
    [filteredFishData]
  );

  const filteredClasses = useMemo(
    () =>
      [
        ...new Set(
          filteredFishData.map((item) => item.class_name).filter((name) => name)
        ),
      ].sort(),
    [filteredFishData]
  );

  const filteredPhyla = useMemo(
    () =>
      [
        ...new Set(
          filteredFishData
            .map((item) => item.phylum_name)
            .filter((name) => name)
        ),
      ].sort(),
    [filteredFishData]
  );

  const filteredCategories = useMemo(
    () =>
      [
        ...new Set(
          categoryData
            .filter((category) =>
              filteredFishData.find((fish) =>
                fishMatchesCategory(fish, category)
              )
            )
            .map((category) => category.common_name)
        ),
      ].sort(),
    [filteredFishData, categoryData]
  );

  const unfilteredScientificNames = useMemo(
    () =>
      [
        ...new Set(
          listingData
            .map((item) => item.scientific_name)
            .filter((name) => !filteredScientificNames.includes(name))
        ),
      ].sort(),
    [listingData, filteredScientificNames]
  );

  const unfilteredSizes = useMemo(
    () =>
      [
        ...new Set(
          listingData
            .map((item) => item.size)
            .filter((name) => !filteredSizes.includes(name))
        ),
      ].sort((a, b) => parseInt(a) - parseInt(b)),
    [listingData, filteredSizes]
  );

  const unfilteredGenera = useMemo(
    () =>
      [
        ...new Set(
          listingData
            .map((item) => item.genus_name)
            .filter((name) => !filteredGenera.includes(name))
            .filter((name) => name)
        ),
      ].sort(),
    [listingData, filteredGenera]
  );

  const unfilteredFamilies = useMemo(
    () =>
      [
        ...new Set(
          listingData
            .map((item) => item.family_name)
            .filter((name) => !filteredFamilies.includes(name))
            .filter((name) => name)
        ),
      ].sort(),
    [listingData, filteredFamilies]
  );

  const unfilteredOrders = useMemo(
    () =>
      [
        ...new Set(
          listingData
            .map((item) => item.order_name)
            .filter((name) => !filteredOrders.includes(name))
            .filter((name) => name)
        ),
      ].sort(),
    [listingData, filteredOrders]
  );

  const unfilteredClasses = useMemo(
    () =>
      [
        ...new Set(
          listingData
            .map((item) => item.class_name)
            .filter((name) => !filteredClasses.includes(name))
            .filter((name) => name)
        ),
      ].sort(),
    [listingData, filteredClasses]
  );

  const unfilteredPhyla = useMemo(
    () =>
      [
        ...new Set(
          listingData
            .map((item) => item.phylum_name)
            .filter((name) => !filteredPhyla.includes(name))
            .filter((name) => name)
        ),
      ].sort(),
    [listingData, filteredPhyla]
  );

  const unfilteredCategories = useMemo(
    () =>
      [
        ...new Set(
          categoryData
            .filter((category) =>
              listingData.find((fish) => fishMatchesCategory(fish, category))
            )
            .map((category) => category.common_name)
            .filter((name) => !filteredCategories.includes(name))
        ),
      ].sort(),
    [listingData, categoryData, filteredCategories]
  );

  useEffect(() => {
    if (dbQueryingExcludingSize) {
      return;
    }

    var oldMinSize = sizeRange[0];
    var oldMaxSize = sizeRange[1];

    // Update the size range dynamically based on filtered data
    const sizes = filteredFishDataExcludingSize
      .map((fish) => fish.size)
      .filter((size) => !isNaN(size) && size >= 0);
    var newMinSize = sizes.length > 0 ? Math.min(...sizes) : 0;
    var newMaxSize = sizes.length > 0 ? Math.max(...sizes) : 36;
    if (newMinSize == 0.5) {
      newMinSize = 0;
    }
    if (newMaxSize == 0.5) {
      newMaxSize = 0;
    }
    if (newMinSize == newMaxSize) {
      if (newMaxSize == 36) {
        newMinSize -= 1;
      } else {
        newMaxSize += 1;
      }
    }
    setMinSize(newMinSize);
    setMaxSize(newMaxSize);
    setSizeRange([newMinSize, newMaxSize]);

    function updateSizeFilter(prev) {
      if (prev[0] == oldMinSize && prev[1] == oldMaxSize) {
        return [newMinSize, newMaxSize];
      }

      const newFilter = [
        Math.min(Math.max(prev[0], newMinSize), newMaxSize),
        Math.max(Math.min(prev[1], newMaxSize), newMinSize),
      ];
      if (newFilter[0] == newFilter[1]) {
        if (newFilter[1] == newMaxSize) {
          newFilter[0] -= 1;
        } else {
          newFilter[1] += 1;
        }
      }
      return newFilter;
    }

    setSizeFilter(updateSizeFilter);
    setTempSizeFilter(updateSizeFilter);
  }, [filteredFishDataExcludingSize, dbQueryingExcludingSize]);

  useEffect(() => {
    if (dbQueryingExcludingSize) {
      return;
    }
    const oldNonNumericSizes = availableNonNumericSizes;
    const sizes = filteredFishDataExcludingSize
      .map((fish) => fish.size)
      .map((size) => sizeNameMap[size])
      .filter((size) => size);
    const uniqueSizes = [...new Set(sizes)];
    setAvailableNonNumericSizes(uniqueSizes);
    setNonNumericSizeFilter((selectedSizes) => {
      const selectedAllPreviousSizes =
        selectedSizes.length === oldNonNumericSizes.length &&
        oldNonNumericSizes.every((size) => selectedSizes.includes(size));
      return selectedAllPreviousSizes
        ? uniqueSizes
        : selectedSizes.filter((size) => uniqueSizes.includes(size));
    });
  }, [filteredFishDataExcludingSize, dbQueryingExcludingSize]);



  const sortSizes = (a, b) => {
    var floatA = parseFloat(a);
    var floatB = parseFloat(b);
    if (isNaN(a)) {
      if (isNaN(b)) {
        return 0;
      } else {
        return 1;
      }
    } else {
      if (isNaN(b)) {
        return -1;
      } else {
        return floatA - floatB;
      }
    }
  };

  function sortFishByOrder(a, b) {
    return (
      sortOptions[sortOrder] ||
      sortOptions[previousSortOrderRef.current] ||
      sortOptions[Object.keys(sortOptions)[0]]
    )(a, b);
  }

  const resetFilters = () => {
    setSearchTerm("");
    setCategoryFilter("");
    setSizeFilter([minSize, maxSize]);
    setTempSizeFilter([minSize, maxSize]);
    setIncludeNumericSizes(true);
    setIncludeNonNumericSizes(true);
    setNonNumericSizeFilter(Object.values(sizeNameMap));
    setScientificFilter("");
    setGenusFilter("");
    setFamilyFilter("");
    setOrderFilter("");
    setClassFilter("");
    setPhylumFilter("");
    setAllowedStores(availableStores);
    setFreshwaterFilter(true);
    setSaltwaterFilter(true);
    setFavoritesOnly(false);
    setNotificationsOnly(false);
    setReefWizardOnly(false);
    setReefWizardTankFilter(null);
    setMinTankSizeMax(null);
    setMaxSizeMax(null);
    setTemperatureRange(null);
    setPhRange(null);
    setCareLevels([]);
    setReefSafeValues([]);
    setPlantSafeValues([]);
    setTemperamentValues([]);
    setDietTraits([]);
    setLifespanRange(null);
    setHasCareGuide("all");
  };

  const handleDigestFilterApply = (tagType, value) => {
    resetFilters();
    setHaveLoadedFiltersFromUrl(true);
    if (tagType === "species") {
      setScientificFilter(value);
    } else if (tagType === "family") {
      setFamilyFilter(value);
    }
  };

  const handleRemoveFilter = (filterKey) => {
    switch (filterKey) {
      case "searchTerm":
        setSearchTerm("");
        break;
      case "scientificFilter":
        setScientificFilter("");
        break;
      case "categoryFilter":
        setCategoryFilter("");
        break;
      case "genusFilter":
        setGenusFilter("");
        break;
      case "familyFilter":
        setFamilyFilter("");
        break;
      case "orderFilter":
        setOrderFilter("");
        break;
      case "classFilter":
        setClassFilter("");
        break;
      case "phylumFilter":
        setPhylumFilter("");
        break;
      case "sizeFilter":
        setSizeFilter([minSize, maxSize]);
        setTempSizeFilter([minSize, maxSize]);
        setIncludeNumericSizes(true);
        setIncludeNonNumericSizes(true);
        setNonNumericSizeFilter(Object.values(sizeNameMap));
        break;
      case "waterType":
        setFreshwaterFilter(true);
        setSaltwaterFilter(true);
        break;
      case "allowedStores":
        setAllowedStores(availableStores);
        break;
      case "favoritesOnly":
        setFavoritesOnly(false);
        break;
      case "notificationsOnly":
        setNotificationsOnly(false);
        break;
      case "reefWizardOnly":
        setReefWizardOnly(false);
        setReefWizardTankFilter(null);
        break;
      case "sortOrder":
        setSortOrder(
          getSortOrderFromExperiment(defaultSortExperiment) ||
            Object.keys(DEFAULT_SORT_OPTIONS)[0]
        );
        break;
      case "careRequirements":
        setMinTankSizeMax(null);
        setMaxSizeMax(null);
        setTemperatureRange(null);
        setPhRange(null);
        setCareLevels([]);
        setReefSafeValues([]);
        setPlantSafeValues([]);
        setTemperamentValues([]);
        setDietTraits([]);
        setLifespanRange(null);
        setHasCareGuide("all");
        break;
      default:
        break;
    }
  };

  // Add ref for listings section
  const fishListingsRef = useRef(null);

  // Display API error if it exists — with retry + auto-retry
  const retryIntervalRef = useRef(null);
  useEffect(() => {
    if (apiError) {
      retryIntervalRef.current = setInterval(() => {
        retryAll();
      }, 10000);
    } else if (retryIntervalRef.current) {
      clearInterval(retryIntervalRef.current);
      retryIntervalRef.current = null;
    }
    return () => {
      if (retryIntervalRef.current) clearInterval(retryIntervalRef.current);
    };
  }, [apiError, retryAll]);

  const renderListingFilters = () => (
    <Filters
      searchTerm={searchTerm}
      setSearchTerm={setSearchTerm}
      categoryFilter={categoryFilter}
      setCategoryFilter={setCategoryFilter}
      sizeFilter={tempSizeFilter}
      onSizeFilterChange={handleTempSizeFilterChange}
      sizeRange={sizeRange}
      allowedStores={allowedStores}
      setAllowedStores={setAllowedStores}
      storesData={storesData}
      availableStores={availableStores}
      filteredCategories={filteredCategories}
      unfilteredCategories={unfilteredCategories}
      filteredSizes={filteredSizes}
      unfilteredSizes={unfilteredSizes}
      sortSizes={sortSizes}
      filteredClasses={filteredClasses}
      unfilteredClasses={unfilteredClasses}
      classFilter={classFilter}
      setClassFilter={setClassFilter}
      filteredOrders={filteredOrders}
      unfilteredOrders={unfilteredOrders}
      orderFilter={orderFilter}
      setOrderFilter={setOrderFilter}
      filteredFamilies={filteredFamilies}
      unfilteredFamilies={unfilteredFamilies}
      familyFilter={familyFilter}
      setFamilyFilter={setFamilyFilter}
      filteredGenera={filteredGenera}
      unfilteredGenera={unfilteredGenera}
      genusFilter={genusFilter}
      setGenusFilter={setGenusFilter}
      filteredScientificNames={filteredScientificNames}
      unfilteredScientificNames={unfilteredScientificNames}
      scientificFilter={scientificFilter}
      setScientificFilter={setScientificFilter}
      speciesMap={speciesMap}
      nonNumericSizeFilter={nonNumericSizeFilter}
      setNonNumericSizeFilter={setNonNumericSizeFilter}
      sortOrder={sortOrder}
      setSortOrder={setSortOrder}
      sortOptions={sortOptions}
      includeNumericSizes={includeNumericSizes}
      setIncludeNumericSizes={setIncludeNumericSizes}
      includeNonNumericSizes={includeNonNumericSizes}
      setIncludeNonNumericSizes={setIncludeNonNumericSizes}
      availableNonNumericSizes={availableNonNumericSizes}
      freshwaterFilter={freshwaterFilter}
      setFreshwaterFilter={setFreshwaterFilter}
      saltwaterFilter={saltwaterFilter}
      setSaltwaterFilter={setSaltwaterFilter}
      favoritesOnly={favoritesOnly}
      setFavoritesOnly={setFavoritesOnly}
      notificationsOnly={notificationsOnly}
      setNotificationsOnly={setNotificationsOnly}
      reefWizardOnly={reefWizardOnly}
      setReefWizardOnly={setReefWizardOnly}
      reefWizardTankFilter={reefWizardTankFilter}
      setReefWizardTankFilter={setReefWizardTankFilter}
      reefWizardTanks={reefWizardTanks}
      careGuidesData={careGuidesData}
      minTankSizeMax={minTankSizeMax}
      setMinTankSizeMax={setMinTankSizeMax}
      maxSizeMax={maxSizeMax}
      setMaxSizeMax={setMaxSizeMax}
      temperatureRange={temperatureRange}
      setTemperatureRange={setTemperatureRange}
      phRange={phRange}
      setPhRange={setPhRange}
      careLevels={careLevels}
      setCareLevels={setCareLevels}
      reefSafeValues={reefSafeValues}
      setReefSafeValues={setReefSafeValues}
      plantSafeValues={plantSafeValues}
      setPlantSafeValues={setPlantSafeValues}
      temperamentValues={temperamentValues}
      setTemperamentValues={setTemperamentValues}
      dietTraits={dietTraits}
      setDietTraits={setDietTraits}
      lifespanRange={lifespanRange}
      setLifespanRange={setLifespanRange}
      availableCareLevels={availableCareLevels}
      availableReefSafe={availableReefSafe}
      availablePlantSafe={availablePlantSafe}
      availableTemperaments={availableTemperaments}
      availableDietTraits={availableDietTraits}
      temperatureBounds={temperatureBounds}
      maxTankSizeBound={maxTankSizeBound}
      maxSizeBound={maxSizeBound}
      phBounds={phBounds}
      lifespanBounds={lifespanBounds}
      hasCareGuide={hasCareGuide}
      setHasCareGuide={setHasCareGuide}
    />
  );

  const activeFiltersProps = {
    filters: {
      searchTerm,
      scientificFilter,
      categoryFilter,
      genusFilter,
      familyFilter,
      orderFilter,
      classFilter,
      phylumFilter,
      sizeFilter,
      sizeMin: sizeFilter[0],
      sizeMax: sizeFilter[1],
      includeNumericSizes,
      includeNonNumericSizes,
      nonNumericSizeFilter,
      freshwaterFilter,
      saltwaterFilter,
      allowedStores,
      favoritesOnly,
      notificationsOnly,
      reefWizardOnly,
      reefWizardTankFilter,
      sortOrder,
      minTankSizeMax,
      maxSizeMax,
      temperatureRange,
      phRange,
      careLevels,
      reefSafeValues,
      plantSafeValues,
      temperamentValues,
      dietTraits,
      lifespanRange,
      hasCareGuide,
    },
    onClearFilters: resetFilters,
    onRemoveFilter: handleRemoveFilter,
    defaultValues: {
      minSize,
      maxSize,
      allowedStores: availableStores,
      availableNonNumericSizes,
      sortOrder:
        getSortOrderFromExperiment(defaultSortExperiment) ||
        Object.keys(DEFAULT_SORT_OPTIONS)[0],
    },
    speciesMap,
    storesData,
    isQuerying: dbQuerying || loadingSearch,
    storesInitialized,
    reefWizardTanks,
  };

  const listingGroups = Object.values(groupedFish).sort(sortFishByOrder);
  const getListingGroupKey = useCallback((group) => group.scientific_name, []);
  const {
    groupVisibility,
    setGroupVisibility,
    setAllGroupsVisibility,
  } = useListingGroupVisibility(listingGroups, getListingGroupKey);

  const handleSelectSpecies = useCallback((scientificName) => {
    setSearchTerm("");
    setScientificFilter(scientificName);
    setAllowedStores(availableStores);
    setGroupVisibility({ [scientificName]: true });

    window.requestAnimationFrame(() => {
      const selectedGroup = fishListingsRef.current?.parentElement?.querySelector(
        ".species-result-expanded"
      );
      (selectedGroup || fishListingsRef.current)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  }, [availableStores, setGroupVisibility]);

  const dealsAdapter = useMemo(() => ({
    isReady: ({ dataLoaded: loaded, isDbQuerying }) =>
      Boolean(
        loaded[dataType] &&
        loaded.historicalPrices &&
        groupedFishForCarousel &&
        !isDbQuerying
      ),
    getDeals: () => buildLivestockDeals(groupedFishForCarousel),
    getDealKey: (deal, index) =>
      `${deal.scientific_name}-${deal.size}-${index}`,
    posthogId: "deal-carousel-deal",
    getAnalyticsEvent: (deal) => ({
      category: "Deals",
      action: "Click Deal",
      label: deal.scientific_name,
    }),
    onSelectDeal: (deal) => handleSelectSpecies(deal.scientific_name),
    getDealImageProps: ({ deal, index, context }) =>
      createImageProps(
        deal.scientific_name,
        context.apiEndpoints,
        context.cachedImageFailed,
        context.markCachedImageFailed,
        context.markImageLoaded,
        {
          id: "fish-image-" + index,
          alt: deal.common_name,
        }
      ),
    getDealImageClassName: ({ deal, context }) =>
      !context.cachedImageFailed[deal.scientific_name] ||
      context.imageLoaded[deal.scientific_name]
        ? ""
        // Keep the fallback image mounted and measurable so lazy loading can finish.
        : "opacity-0",
    renderDealText: renderLivestockDealText,
  }), [dataType, groupedFishForCarousel, handleSelectSpecies]);

  const priceDigestConfig = {
    show: showPriceDigest,
    dataType,
    digestData: dataType === "plants" ? plantDigest : dataType === "inverts" ? invertDigest : fishDigest,
    onFilterApply: handleDigestFilterApply,
  };

  const personalListEmptyState = {
    active: favoritesOnly || notificationsOnly,
    isAuthenticated,
    authenticatedMessage: `You don't have any favorited ${dataType} or notifications set up. Click on the heart or bell to add some!`,
    unauthenticatedMessage: "Please log in to set up favorites and notifications!",
  };

  const reefWizardEmptyState = {
    active: reefWizardOnly,
    isAuthenticated,
    dataType,
    hasRecommendations: Boolean(reefWizardRecommendations?.length),
  };

  const aboutContent = (
    <>
      <p className="mt-2">
        Welcome to{" "}
        <i>
          <b>Sea What You Save</b>
        </i>
        , your one-stop shop for finding the best prices on {dataType}{" "}
        for your aquarium! We've searched far and wide across the sevens seas
        of the internet to find you the best deals from online aquarium stores.
      </p>
      <p className="mt-4">
        Use the filters below to find the perfect {dataType} for your aquarium.
        You can search by name, category, size, and more. Once you've found the
        {" "}{dataType} you're interested in, click on it to see the best prices
        from various sellers.
      </p>
      <p className="mt-4">
        <i>
          <b>Sea What You Save</b>
        </i>{" "}
        is not affiliated with any of the stores and does not sell {dataType}{" "}
        directly. We simply provide a platform for you to compare prices and
        find the best deals. We do not guarantee the availability or quality of
        the {dataType} listed on our site. Please check the seller's website for
        the most up-to-date information.
      </p>
    </>
  );

  const overflowContent = (
    <div className="bg-gray-50 p-3 rounded-lg shadow">
      <p>
        Wow, you must really be fishing for a good deal! We only show the first
        100 results. Add more filters to narrow down your search and see more.
      </p>
    </div>
  );

  return (
    <>
      <SEO
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonical={seoCanonical}
      />
      <ComparisonPageFrame
        apiError={apiError}
        errorTitle="Something went wrong loading listings."
        errorSubtitle="Retrying automatically..."
        onRetry={retryAll}
        renderFilters={renderListingFilters}
        groupVisibility={groupVisibility}
        setAllGroupsVisibility={setAllGroupsVisibility}
        onResetFilters={resetFilters}
        aboutInitiallyVisible={true}
        aboutContent={aboutContent}
        filtered={isFiltered}
        headingFiltered={headingFiltered}
        descriptionFiltered={descriptionFiltered}
        headingUnfiltered={headingUnfiltered}
        descriptionUnfiltered={descriptionUnfiltered}
        headingRef={fishListingsRef}
        activeFiltersProps={activeFiltersProps}
        dealsAdapter={dealsAdapter}
        dealsIsDbQuerying={dbQueryingForCarousel || loadingSearch}
        priceDigestConfig={priceDigestConfig}
        personalListEmptyState={personalListEmptyState}
        reefWizardEmptyState={reefWizardEmptyState}
        groups={listingGroups}
        groupKey={getListingGroupKey}
        renderGroup={(group) => (
          <FishListing
            group={group}
            groupVisibility={groupVisibility}
            setGroupVisibility={setGroupVisibility}
            storesData={storesData}
            getFishProductUrl={getFishProductUrl}
          />
        )}
        groupsClassName="species-results-grid"
        getGroupClassName={(group) =>
          groupVisibility[group.scientific_name]
            ? "species-result-expanded"
            : "species-result-collapsed"
        }
        loading={loadingListings || loadingSearch || isAnyDbQuerying}
        emptyText={`No ${dataType} matching your criteria`}
        overflowContent={overflowContent}
      />
    </>
  );

};

export default ListingPriceComparison;
